import React, { useState, useEffect, useRef, Fragment } from 'react';
import cloneDeep from 'lodash.clonedeep';
import nearestPoint from '@turf/nearest-point';
import haversine from 'haversine-distance';
import { point as createPoint, featureCollection } from '@turf/helpers';
import actions from 'actions';
import MapComponent from 'components/Map';
import Slider from 'components/Slider';
import PendingPromisses from 'components/PendingPromisses';
import SpotForm from './SpotForm';
import MainPhotoForm from './MainPhotoForm';
import TopPhotoForm from './TopPhotoForm';
import DescriptionForm from './DescriptionForm';
import EasyToAssign from './EasyToAssign';
import config from 'config';
import utilsCoors from 'utils/coors';
import './styles.scss';

const ACTIONS = {
  NONE: 'NONE',
  SHOW_SPOT: 'SHOW_SPOT',
  CREATE: {
    SET_GPS: 'CREATE__SET_GPS',
    SPOT_FORM: 'CREATE__SPOT_FORM',
  },
  ASSIGN: 'ASSIGN',
  REJECT: 'REJECT',
  EDIT: 'EDIT',
};

const PHOTO_TYPE = {
  UNSPLASH: 'UNSPLASH',
  USER: 'USER',
  DETECTIVE: 'DETECTIVE',
};

const FINAL_DECISION_TYPES = new Set(['create_spot', 'assign_spot', 'reject']);

const getQueueTelemetryProperties = metadata => {
  const count = metadata?.count;
  const ageSampleCount = metadata?.ageSampleCount;
  const oldestItemAt = metadata?.oldestItemAt;
  const numericCount = count === null || typeof count === 'undefined' ? null : Number(count);
  const numericAgeSampleCount =
    ageSampleCount === null || typeof ageSampleCount === 'undefined'
      ? null
      : Number(ageSampleCount);
  const numericOldestItemAt =
    oldestItemAt === null || typeof oldestItemAt === 'undefined'
      ? null
      : Number(oldestItemAt);
  const now = Date.now();

  return {
    ...(Number.isInteger(numericCount) && numericCount >= 0
      ? { queue_count: numericCount }
      : {}),
    ...(Number.isInteger(numericAgeSampleCount) && numericAgeSampleCount >= 0
      ? { queue_age_sample_count: numericAgeSampleCount }
      : {}),
    ...(Number.isFinite(numericOldestItemAt) &&
    numericOldestItemAt > 0 &&
    numericOldestItemAt <= now
      ? { oldest_item_age_seconds: Math.round((now - numericOldestItemAt) / 1000) }
      : {}),
  };
};

const getPhotoQueue = photo => {
  if (photo && photo.detective) {
    return 'detective';
  }

  return photo && photo.type === PHOTO_TYPE.USER ? 'user_photos' : 'unsplash';
};

const getStage = action => {
  const stages = {
    [ACTIONS.NONE]: 'review',
    [ACTIONS.CREATE.SET_GPS]: 'set_gps',
    [ACTIONS.CREATE.SPOT_FORM]: 'create_spot',
    [ACTIONS.ASSIGN]: 'assign_spot',
    [ACTIONS.REJECT]: 'reject_reason',
  };

  return stages[action] || 'review';
};

const getPhotoPosition = photo => {
  const location = photo && photo.location;
  const lat = Number(photo && (photo.lat ?? (location && location.position?.latitude)));
  const lng = Number(photo && (photo.lng ?? (location && location.position?.longitude)));

  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const getNearbySpots = (photo, spots) => {
  const photoPosition = getPhotoPosition(photo);

  if (!photoPosition || !Array.isArray(spots) || spots.length === 0) {
    return [];
  }

  return spots
    .map(spot => ({ spot, distance: haversine(photoPosition, spot) }))
    .filter(item => item.distance < 300)
    .sort((a, b) => a.distance - b.distance);
};

const spotsReferToSameSpot = (left, right) =>
  Boolean(
    left &&
      right &&
      (left.id === right.id ||
        (left._pendingCreationKey &&
          left._pendingCreationKey === right._pendingCreationKey))
  );

const getOptionMatchRank = (options, value) => {
  const normalizedValue = String(value || '')
    .trim()
    .toLocaleLowerCase();

  if (!normalizedValue || !Array.isArray(options)) {
    return null;
  }

  const index = options.findIndex(option =>
    String(option && (option.value || option))
      .trim()
      .toLocaleLowerCase() === normalizedValue
  );

  return index === -1 ? null : index + 1;
};

const getEvidenceProperties = ({ photo, spots, decisionType, selectedSpot, name, from }) => {
  const aiReview = (photo && photo.aiReview) || {};
  const aiModeration = aiReview.moderation || {};
  const aiVision = aiReview.vision || {};
  const nearbySpots = getNearbySpots(photo, spots);
  const nearbyAcceptedIndex = selectedSpot
    ? nearbySpots.findIndex(item => item.spot.id === selectedSpot.id)
    : -1;
  const isPositiveDecision = ['create_spot', 'assign_spot'].includes(decisionType);
  const isNegativeDecision = decisionType === 'reject';
  const decidedRecommendation = isPositiveDecision ? 'publish' : isNegativeDecision ? 'reject' : null;
  const aiEvidenceAvailable = Boolean(aiReview.moderation || aiReview.vision || aiReview.google);
  const aiRecommendation = aiReview.moderation
    ? aiModeration.flagged === true
      ? 'reject'
      : 'publish'
    : 'none';
  const aiNameMatchRank = getOptionMatchRank(
    aiVision.placeNameOptionDetails || aiVision.placeNameOptions,
    name
  );
  const aiFromMatchRank = getOptionMatchRank(
    aiVision.fromOptionDetails || aiVision.fromOptions,
    from
  );

  return {
    ai_evidence_available: aiEvidenceAvailable,
    ai_recommendation: aiRecommendation,
    ...(decidedRecommendation && aiRecommendation !== 'none'
      ? { ai_evidence_accepted: aiRecommendation === decidedRecommendation }
      : {}),
    ...(aiNameMatchRank ? { ai_name_match_rank: aiNameMatchRank } : {}),
    ...(aiFromMatchRank ? { ai_from_match_rank: aiFromMatchRank } : {}),
    nearby_evidence_evaluated: spots.length > 0,
    nearby_evidence_available: nearbySpots.length > 0,
    nearby_spot_count: nearbySpots.length,
    ...(['create_spot', 'assign_spot'].includes(decisionType) && nearbySpots.length > 0
      ? { nearby_evidence_accepted: nearbyAcceptedIndex >= 0 }
      : {}),
    ...(nearbyAcceptedIndex >= 0 ? { nearby_accepted_rank: nearbyAcceptedIndex + 1 } : {}),
  };
};

const getTelemetryErrorType = error => {
  const status = Number(error && error.status);

  if (!window.navigator.onLine || (error && error.name === 'TypeError')) {
    return 'network';
  }

  if ([401, 403].includes(status)) {
    return 'authorization';
  }

  if (status >= 400 && status < 500) {
    return 'validation';
  }

  if (status >= 500) {
    return 'server';
  }

  return 'unknown';
};

const getFinalizationProperties = result => {
  const finalization = result && result.moderationFinalization;

  if (!finalization) {
    return {};
  }

  return {
    backend_finalization_counted: Boolean(finalization.counted),
    ...(finalization.sequence
      ? { backend_finalization_sequence: Number(finalization.sequence) }
      : {}),
    ...(finalization.finalizedAt
      ? { backend_finalized_at_ms: Number(finalization.finalizedAt) }
      : {}),
    ...(finalization.uploadToFinalDurationSeconds !== null &&
    typeof finalization.uploadToFinalDurationSeconds !== 'undefined' &&
    Number.isFinite(Number(finalization.uploadToFinalDurationSeconds))
      ? { upload_to_final_duration_seconds: Number(finalization.uploadToFinalDurationSeconds) }
      : {}),
  };
};

const POI_INFORMATION_CLOSE_SELECTOR = '.gm-style-iw button.gm-ui-hover-effect';

const closePoiInformation = () => {
  const closeButton = document.querySelector(POI_INFORMATION_CLOSE_SELECTOR);

  if (closeButton) {
    closeButton.click();
  }
};

const REJECTION_CAUSES = [
  { value: 0, label: 'Wrong GPS', 'data-action-key': 2 },
  { value: 1, label: 'Bad Quality' },
  { value: 2, label: 'Drone' },
  { value: 3, label: 'Too many photos from that spot' },
  { value: 4, label: 'Private' },
  { value: 5, label: 'Selfie' },
  { value: 6, label: 'Temporal' },
  { value: 7, label: 'Elieser' },
  { value: 8, label: 'Not a Spot', 'data-action-key': 3 },
  { value: 9, label: 'Already Approved' },
  { value: 10, label: 'Not Interesting Enough', 'data-action-key': 4 },
  { value: 11, label: 'Not Centerd' },
  { value: 12, label: 'Too Paronamic' },
  { value: 13, label: 'Too Much Edited' },
  { value: 14, label: 'Border Too Big' },
  { value: 15, label: 'Watermark Too Big' },
  { value: 99, label: 'Unknown' },
];

const getRejectionCauseLabel = cause => {
  const match = REJECTION_CAUSES.find(item => item.value === Number(cause));

  return match ? match.label : `Cause ${cause}`;
};

let listOfPossibleToAssign = [];

const getModerationPhotoUrl = photo => {
  if (!photo) {
    return null;
  }

  const rawUrl = photo.type === PHOTO_TYPE.UNSPLASH ? photo.urls && photo.urls.regular : photo.url;

  if (!rawUrl) {
    return null;
  }

  return rawUrl
    .replace('.jpg', '.webp')
    .replace(
      'https://storage.googleapis.com/mari-a5cc7.appspot.com',
      'https://statics.getnofilter.com'
    );
};

const preloadPhotoInBrowser = (photo, preloadedUrls) => {
  const url = getModerationPhotoUrl(photo);

  if (!url || preloadedUrls.has(url)) {
    return;
  }

  const img = new Image();
  img.src = url;
  preloadedUrls.add(url);
};

const Moderation = () => {
  const [photos, setPhotos] = useState([]);
  const [spots, setSpots] = useState([]);
  const [cityTours, setCityTours] = useState([]);
  const [userPhotos, setUserPhotos] = useState([]);

  // Selecteds
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [selectedUserPhoto, setSelectedUserPhoto] = useState(null);

  //
  const [pointsToShow, setPointsToShow] = useState([]);

  const [action, setAction] = useState(ACTIONS.NONE);

  // Data used to Create and Edit a Spot
  const [id, setId] = useState(null);
  const [name, setName] = useState('');
  const [from, setFrom] = useState('');
  const [slug, setSlug] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [isGPSCorrect, setIsGPSCorrect] = useState(true);
  const [newGPS, setNewGPS] = useState({});
  const [selectedPoi, setSelectedPoi] = useState(null);

  // Data used to Assign photo to Spot
  const [assignTo, setAssignTo] = useState(null);

  // Data used to Reject
  const [cause, setCause] = useState('');

  // Data used for filters
  const [showPendings, setShowPendings] = useState(false);
  const [showPendingsFromUsers, setShowPendingsFromUsers] = useState(false);
  const [showRejecteds, setShowRejecteds] = useState(false);
  const [showSpots, setShowSpots] = useState(false);
  const [showNulls, setShowNulls] = useState(false);
  const [coors, setCoors] = useState({
    sw: {
      lat: 22,
      lng: -129,
    },
    ne: {
      lat: 50,
      lng: -66,
    },
  });
  const [isSettingSW, setIsSettingSW] = useState(false);
  const [isSettingNE, setIsSettingNE] = useState(false);

  // Others
  const [isCreating, setIsCreating] = useState(false);
  const [center, setCenter] = useState(undefined);
  const [zoom, setZoom] = useState(undefined);
  const [spotWithNoMainPhotoId, setSpotWithNoMainPhotoId] = useState(null);
  const [spotWithNoIsTop, setSpotWithNoIsTop] = useState(null);
  const [spotWithNoDescription, setSpotWithNoDescription] = useState(null);
  const [possibleToAssign, setPossibleToAssign] = useState(null);
  const [addresses, setAddresses] = useState(null);
  const preloadedPhotoUrls = useRef(new Set());
  const moderationTelemetry = useRef(null);
  const shownPhoto = useRef(null);
  const decisionAttempts = useRef(new Map());
  const pendingCreationPhotoIds = useRef(new Set());

  const isSettingGPS = action === ACTIONS.CREATE.SET_GPS;
  const isCreatingSpotWithWrongGPS = !isGPSCorrect && action === ACTIONS.CREATE.SPOT_FORM;
  const isEditing = action === ACTIONS.EDIT;
  const isNewPositionShown = isSettingGPS || isCreatingSpotWithWrongGPS || isEditing;
  const canFillSpotFromPoi = [ACTIONS.CREATE.SPOT_FORM, ACTIONS.EDIT].includes(action);

  useEffect(() => {
    const session = actions.moderationTelemetry.startSession('moderation');
    moderationTelemetry.current = session;

    return () => {
      session.stop('unmount');
      moderationTelemetry.current = null;
    };
  }, []);

  useEffect(() => {
    const photo = selectedPhoto || selectedUserPhoto;

    if (!photo || !moderationTelemetry.current) {
      return;
    }

    const internalPhotoKey = `${getPhotoQueue(photo)}:${photo.id}`;
    const stage = getStage(action);

    if (!shownPhoto.current || shownPhoto.current.key !== internalPhotoKey) {
      shownPhoto.current = {
        key: internalPhotoKey,
        shownAt: Date.now(),
        stage: null,
      };

      moderationTelemetry.current.track('MODERATION_PHOTO_SHOWN', {
        queue: getPhotoQueue(photo),
        stage: 'review',
        ...getEvidenceProperties({ photo, spots, name, from }),
      });
    }

    if (shownPhoto.current.stage !== stage) {
      shownPhoto.current.stage = stage;
      moderationTelemetry.current.track('MODERATION_STAGE_ENTERED', {
        queue: getPhotoQueue(photo),
        stage,
        seconds_since_photo_shown: Number(
          ((Date.now() - shownPhoto.current.shownAt) / 1000).toFixed(1)
        ),
        ...getEvidenceProperties({ photo, spots, name, from }),
      });
    }
  }, [action, from, name, selectedPhoto, selectedUserPhoto, spots]);

  useEffect(() => {
    const photo = possibleToAssign && possibleToAssign.photo;

    if (!photo || !moderationTelemetry.current) {
      return;
    }

    const internalPhotoKey = `${getPhotoQueue(photo)}:${photo.id}`;

    if (!shownPhoto.current || shownPhoto.current.key !== internalPhotoKey) {
      shownPhoto.current = {
        key: internalPhotoKey,
        shownAt: Date.now(),
        stage: 'easy_assign',
      };
      moderationTelemetry.current.track('MODERATION_PHOTO_SHOWN', {
        queue: getPhotoQueue(photo),
        stage: 'easy_assign',
        ...getEvidenceProperties({ photo, spots, name: '', from: '' }),
      });
      moderationTelemetry.current.track('MODERATION_STAGE_ENTERED', {
        queue: getPhotoQueue(photo),
        stage: 'easy_assign',
        seconds_since_photo_shown: 0,
        ...getEvidenceProperties({ photo, spots, name: '', from: '' }),
      });
    }
  }, [possibleToAssign, spots]);

  useEffect(() => {
    const _onKeyPress = e => {
      if (
        // ALWAYS CHECK THIS!
        document.querySelector('.TopPhotoForm') ||
        document.querySelector('.MainPhotoForm') ||
        document.querySelector('.DescriptionForm') ||
        document.querySelector('.EasyToAssign')
      ) {
        return;
      }

      if (e.key === 'Escape') {
        const cancelButton = document.querySelector('button.cancel');

        if (cancelButton) {
          cancelButton.click();
        }
      } else {
        const actionButton = document.querySelector(`[data-action-key="${e.key}"]`);
        // event.metaKey is CMD (CMD + 3, for example)
        if (actionButton && !e.metaKey && actionButton.click) {
          e.preventDefault();
          actionButton.click();
        }
      }
    };

    (async () => {
      const { data } = await fetch(
        'https://storage.googleapis.com/mari-a5cc7.appspot.com/cityTours/index-v1.json?rand=' +
          Math.random()
      ).then(r => r.json());

      setCityTours(
        data.cityTours.map(x => ({
          ...x,
          type: 'CITYTOUR',
          pointType: 'citytour',
          lat: x.location.lat,
          lng: x.location.lng,
        }))
      );
    })();

    document.addEventListener('keydown', _onKeyPress, false);
    return () => {
      document.removeEventListener('keydown', _onKeyPress, false);
    };
  }, []);

  useEffect(() => {
    window.spots = spots;

    // if (selectedSpot) {
    //   setSelectedSpot(spots.find(x => x.id === selectedSpot.id));
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spots]);

  useEffect(() => {
    window.userPhotos = userPhotos;

    // if (selectedUserPhoto) {
    //   setSelectedUserPhoto(userPhotos.find(x => x.id === selectedUserPhoto.id));
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPhotos]);

  useEffect(() => {
    window.photos = photos;

    // if (selectedPhoto) {
    //   setSelectedPhoto(photos.find(x => x.id === selectedPhoto.id));
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos]);

  useEffect(() => {
    if (!selectedUserPhoto) {
      return;
    }

    const nextUserPhotoPoints = userPhotos
      .filter(x => x._status === 0 && x.id !== selectedUserPhoto.id)
      .map(x => createPoint(utilsCoors.toGeoJSONPosition(x), { photo: x }));

    if (nextUserPhotoPoints.length === 0) {
      return;
    }

    const nextUserPhotoPoint = nearestPoint(
      createPoint(utilsCoors.toGeoJSONPosition(selectedUserPhoto)),
      featureCollection(nextUserPhotoPoints)
    );

    if (!nextUserPhotoPoint || !nextUserPhotoPoint.properties || !nextUserPhotoPoint.properties.photo) {
      return;
    }

    preloadPhotoInBrowser(nextUserPhotoPoint.properties.photo, preloadedPhotoUrls.current);
  }, [selectedUserPhoto, userPhotos]);

  const _getCloserPending = selectedPhoto => {
    const thePhotos = [
      ...photos.filter(x => x._status === 3),
      ...userPhotos.filter(x => x._status === 0),
    ]
      .filter(x => x.id !== selectedPhoto.id)
      .map(x => createPoint(utilsCoors.toGeoJSONPosition(x), { photo: x }));

    if (thePhotos.length === 0) {
      return null;
    }

    const photoPoints = featureCollection(thePhotos);

    const centerPoint = createPoint(utilsCoors.toGeoJSONPosition(selectedPhoto));
    const closer = nearestPoint(centerPoint, photoPoints);
    return closer;
  };

  const _moveToNextPhoto = lastPhoto => {
    if (!lastPhoto) {
      reset();
      return;
    }

    // Get next photo pending
    const nextPhotoPoint = _getCloserPending(lastPhoto);

    if (!nextPhotoPoint || !nextPhotoPoint.properties || !nextPhotoPoint.properties.photo) {
      reset();
      return;
    }

    const nextPhoto = nextPhotoPoint.properties.photo;
    preloadPhotoInBrowser(nextPhoto, preloadedPhotoUrls.current);

    // Simulate click on it
    _onPointClick(nextPhoto);

    // Move Map
    setCenter({
      lat: nextPhoto.lat,
      lng: nextPhoto.lng,
    });
  };

  const reset = (callback = () => {}) => {
    if (document.querySelector("button[title='Close']")) {
      document.querySelector("button[title='Close']").click();
    }

    setSelectedSpot(null);
    setSelectedPhoto(null);
    setSelectedUserPhoto(null);

    setId(null);
    setName('');
    setFrom('');
    setSlug('');
    setCity('');
    setCountry('');
    setIsGPSCorrect(true);
    setNewGPS({});
    setSelectedPoi(null);
    setAssignTo(null);
    setCause('');

    setAction(ACTIONS.NONE);

    setAddresses(null);

    if (typeof callback === 'function') {
      callback();
    }
  };

  const trackQueueLoaded = (queue, queueMetadata, loadStartedAt) => {
    moderationTelemetry.current?.track('MODERATION_QUEUE_LOADED', {
      queue,
      stage: 'queue',
      load_succeeded: true,
      load_duration_ms: Date.now() - loadStartedAt,
      ...getQueueTelemetryProperties(queueMetadata),
    });
  };

  const trackQueueLoadFailed = (queue, error, loadStartedAt) => {
    moderationTelemetry.current?.track('MODERATION_QUEUE_LOAD_FAILED', {
      queue,
      stage: 'queue',
      load_succeeded: false,
      load_duration_ms: Date.now() - loadStartedAt,
      error_type: getTelemetryErrorType(error),
    });
  };

  const getUnsplashPhotosPending = async () => {
    const loadStartedAt = Date.now();

    try {
      const { photos: hydrateds, queue } = await actions.photos.getHydratedsWithLocation({
        limit: 10000,
        coors,
      });

      const photos = hydrateds
        .filter(
          x =>
            coors.ne.lat > x.location.position.latitude &&
            coors.sw.lat < x.location.position.latitude
        )
        .map(x => {
          const photo = {
            ...x,
            lat: x.location.position.latitude,
            lng: x.location.position.longitude,
            type: PHOTO_TYPE.UNSPLASH,
            pointType: 'photo',
          };

          return photo;
        });

      setPhotos(photos);
      trackQueueLoaded('unsplash', queue, loadStartedAt);

      checkIfEasyToAssign(userPhotos, photos, spots);
    } catch (error) {
      trackQueueLoadFailed('unsplash', error, loadStartedAt);
      throw error;
    }
  };

  const getDetectivePhotosPending = async () => {
    const loadStartedAt = Date.now();

    try {
      const { photos: allPhotos, queue } = await actions.photos.detective.getReadys();

      const photos = allPhotos.map(x => {
        const photo = {
          ...x,
          lat: x.location.position.latitude,
          lng: x.location.position.longitude,
          type: PHOTO_TYPE.UNSPLASH,
          pointType: 'photo',
          detective: x.detective,
        };

        return photo;
      });

      setPhotos(photos);
      trackQueueLoaded('detective', queue, loadStartedAt);

      checkIfEasyToAssign(userPhotos, photos, spots);
    } catch (error) {
      trackQueueLoadFailed('detective', error, loadStartedAt);
      throw error;
    }
  };

  const getUserPhotosPending = async () => {
    const loadStartedAt = Date.now();

    try {
      const { photos: hydrateds, queue } = await actions.userPhotos.getHydratedsWithLocation({
        limit: 10000,
      });

      const userPhotos = hydrateds.map(x => {
        const photo = {
          ...x,
          lat: x.location.lat,
          lng: x.location.lng,
          type: PHOTO_TYPE.USER,
          pointType: 'userPhoto',
        };

        return photo;
      });

      setUserPhotos(userPhotos);
      trackQueueLoaded('user_photos', queue, loadStartedAt);
    } catch (error) {
      trackQueueLoadFailed('user_photos', error, loadStartedAt);
      throw error;
    }
  };

  const getSpots = async () => {
    // The spot list no longer carries a description. It used to carry one whose
    // every value had been replaced by "?", purely so this page could ask which
    // locales existed - 8.4 MB shipped to find the ten spots that need writing.
    // The v19 build works the same two answers out while it already holds every
    // spot, and publishes them as two short lists of ids.
    const [lightSpots, todo] = await Promise.all([
      actions.spots.getAllLight(),
      actions.spots.getDescriptionTodo().catch(() => null),
    ]);
    const areSlugsMissing = lightSpots.some(x => !x.slug);

    let slugById = new Map();

    if (areSlugsMissing) {
      let slugSpots = await actions.spots.getAllSlugsFromSpotsV19().catch(() => []);

      if (slugSpots.length === 0) {
        slugSpots = await actions.spots.getAllSlugs();
      }

      slugById = new Map(slugSpots.map(s => [s.id, s.slug]));
    }

    const spots = lightSpots.map(x => ({
      ...x,
      slug: x.slug || slugById.get(x.id),
      pointType: 'spot',
      // This is just to use from Developers Console
      goThere: () => {
        setCenter({ lat: x.lat, lng: x.lng });
      },
    }));

    setSpots(spots);

    const spotById = new Map(spots.map(spot => [spot.id, spot]));
    const firstFromTodo = ids =>
      (ids || []).map(id => spotById.get(id)).find(Boolean) || null;

    // mainPhotoId still comes with the spot list, so it is read from there and
    // the file only has to answer for the descriptions.
    setSpotWithNoMainPhotoId(spots.find(x => !x.mainPhotoId));
    setSpotWithNoDescription(todo ? firstFromTodo(todo.noDescription) : null);

    checkIfEasyToAssign(userPhotos, photos, spots);

    // Request some translations
    if (!todo) {
      return;
    }

    (todo.missingTranslations || []).slice(0, 450 / 2).forEach((spotId, index) => {
      if (!spotById.has(spotId)) {
        return;
      }

      setTimeout(() => {
        actions.spots.translateDescriptionById(spotId);
      }, 2000 * index);
    });
  };

  async function checkIfEasyToAssign(userPhotos, photos, spots) {
    try {
      const proms = [...userPhotos, ...photos].map(async photo => {
        let nearSpots = [];

        spots.forEach(spot => {
          // Compute distance from the main photo or user's photo
          let distance = haversine(
            photo.type === PHOTO_TYPE.UNSPLASH ? photo.location.position : photo.location,
            spot
          );

          // If there are detective points, check and keep the shortest distance
          if (photo.detective) {
            photo.detective.forEach(detective => {
              distance = Math.min(distance, haversine(detective.position, spot));
            });
          }

          if (distance < 300) {
            nearSpots.push({ ...spot, distance });
          }
        });

        if (nearSpots.length > 0) {
          nearSpots.sort((a, b) => a.distance - b.distance);

          const proms = nearSpots.map(async spot => {
            await hydrateSpot(spot);
          });

          await Promise.all(proms);

          listOfPossibleToAssign.push({ photo, spots: nearSpots });
        }
      });

      await Promise.all(proms);

      if (listOfPossibleToAssign.length > 0) {
        setSelectedPhoto(null);
        setSelectedUserPhoto(null);

        setPossibleToAssign(listOfPossibleToAssign[0]);
        listOfPossibleToAssign = listOfPossibleToAssign.slice(1);
      }
    } catch (error) {
      console.error(error);
      window.alert(error.message);
    }
  }

  const addPendingSpotToNearbyAssignments = (pendingSpot, sourcePhoto) => {
    const nearbyPhotos = [
      ...photos.filter(item => item._status === 3),
      ...userPhotos.filter(item => item._status === 0),
    ]
      .filter(item => item.id !== sourcePhoto.id)
      .map(item => {
        const position = getPhotoPosition(item);

        return {
          photo: item,
          distance: position ? haversine(position, pendingSpot) : Number.POSITIVE_INFINITY,
        };
      })
      .filter(item => item.distance < 300)
      .sort((left, right) => left.distance - right.distance);

    if (nearbyPhotos.length === 0) {
      return;
    }

    const addSpotToEntry = (entry, distance) => {
      if (entry.spots.some(item => spotsReferToSameSpot(item, pendingSpot))) {
        return entry;
      }

      return {
        ...entry,
        spots: [...entry.spots, { ...pendingSpot, distance }].sort(
          (left, right) => left.distance - right.distance
        ),
      };
    };

    let nextEntry = possibleToAssign;

    nearbyPhotos.forEach(({ photo: nearbyPhoto, distance }) => {
      if (nextEntry && nextEntry.photo.id === nearbyPhoto.id) {
        nextEntry = addSpotToEntry(nextEntry, distance);
        return;
      }

      const queuedIndex = listOfPossibleToAssign.findIndex(
        entry => entry.photo.id === nearbyPhoto.id
      );

      if (queuedIndex >= 0) {
        listOfPossibleToAssign[queuedIndex] = addSpotToEntry(
          listOfPossibleToAssign[queuedIndex],
          distance
        );
      } else {
        listOfPossibleToAssign.push({
          photo: nearbyPhoto,
          spots: [{ ...pendingSpot, distance }],
        });
      }
    });

    if (!nextEntry && listOfPossibleToAssign.length > 0) {
      nextEntry = listOfPossibleToAssign[0];
      listOfPossibleToAssign = listOfPossibleToAssign.slice(1);
    }

    setPossibleToAssign(nextEntry);
  };

  const _onPointHover = photo => {
    if (!getModerationPhotoUrl(photo)) {
      console.log('$$ photo with no hover', photo);
      return;
    }

    preloadPhotoInBrowser(photo, preloadedPhotoUrls.current);
  };

  const _onPointClick = photo => {
    console.info('Photo Clicked', photo);

    reset(() => {
      setNewGPS({ lat: photo.lat, lng: photo.lng });

      if (photo.type === PHOTO_TYPE.UNSPLASH) {
        setSelectedPhoto(photo);
        setSelectedUserPhoto(null);

        // if (photo.location && photo.location.name) {
        //   setName(photo.location.name.split(',')[0]);
        // }
      } else if (photo.type === PHOTO_TYPE.USER) {
        setSelectedUserPhoto(photo);
        setSelectedPhoto(null);

        setName((photo.name || '').trim());
        setFrom((photo.from || '').trim());

        // precache for the app
        const tempImg = new Image();
        tempImg.src = photo.url
          .replace('.jpg', '.webp')
          .replace('/regular/', '/small/')
          .replace(
            'https://storage.googleapis.com/mari-a5cc7.appspot.com',
            'https://statics.getnofilter.com'
          );
      } else if (photo.type === PHOTO_TYPE.DETECTIVE) {
        console.log('$$ NO ACTION DEFINED YET', photo);
      }
    });
  };

  const hydrateSpot = async spot => {
    if (spot.isHydrated) {
      return;
    }

    const { spot: fullSpot } = await actions.spots.getHydratedById(spot.id);

    Object.assign(spot, fullSpot, { isHydrated: true });

    // const newSpot = { ...spot, ...fullSpot, isHydrated: true };

    setSpots(spots => spots.map(x => (x.id === spot.id ? spot : x)));

    if (selectedSpot && spot.id === selectedSpot.id) {
      setSelectedSpot(spot);
    }
  };

  const _onSpotClick = async spot => {
    await hydrateSpot(spot);

    console.info('Spot Clicked', spot);

    if (action === ACTIONS.ASSIGN) {
      setAssignTo(spot);
    } else {
      setAction(ACTIONS.SHOW_SPOT);
    }

    setSelectedSpot(spot);

    setTimeout(() => {
      document.querySelector('.Map button.approve').focus();
    }, 0);
  };

  const _onMapChange = ({ zoom, center }) => {
    setZoom(zoom);
    setCenter(center);
  };

  const beginDecision = (photo, decisionType, decisionSpot, stageOverride) => {
    const internalPhotoKey = `${getPhotoQueue(photo)}:${photo.id}`;
    const attemptNumber = Number(decisionAttempts.current.get(internalPhotoKey) || 0) + 1;
    const now = Date.now();
    const shownAt =
      shownPhoto.current && shownPhoto.current.key === internalPhotoKey
        ? shownPhoto.current.shownAt
        : now;
    const properties = {
      decision_id: actions.moderationTelemetry.createId(),
      queue: getPhotoQueue(photo),
      stage: stageOverride || getStage(action),
      decision_type: decisionType,
      is_final_decision: FINAL_DECISION_TYPES.has(decisionType),
      decision_duration_seconds: Number(((now - shownAt) / 1000).toFixed(1)),
      attempt_number: attemptNumber,
      retry_count: attemptNumber - 1,
      is_retry: attemptNumber > 1,
      ...getEvidenceProperties({
        photo,
        spots,
        decisionType,
        selectedSpot: decisionSpot,
        name,
        from,
      }),
    };

    decisionAttempts.current.set(internalPhotoKey, attemptNumber);
    moderationTelemetry.current?.track('MODERATION_DECISION_ATTEMPTED', properties);

    return {
      internalPhotoKey,
      requestStartedAt: now,
      properties,
    };
  };

  const completeDecision = (decision, result) => {
    decisionAttempts.current.delete(decision.internalPhotoKey);
    moderationTelemetry.current?.track('MODERATION_DECISION_SUCCEEDED', {
      ...decision.properties,
      request_duration_ms: Date.now() - decision.requestStartedAt,
      ...getFinalizationProperties(result),
    });
  };

  const failDecision = (decision, error) => {
    moderationTelemetry.current?.track('MODERATION_DECISION_FAILED', {
      ...decision.properties,
      request_duration_ms: Date.now() - decision.requestStartedAt,
      error_type: getTelemetryErrorType(error),
    });
  };

  const _onReject = causeOverride => {
    const photo = selectedPhoto || selectedUserPhoto;
    const nextPhotoToClick = cloneDeep(photo);
    const isPrimitiveOverride = ['string', 'number'].includes(typeof causeOverride);
    const rejectionCause = isPrimitiveOverride ? causeOverride : cause;
    const decision = beginDecision(photo, 'reject', null, 'reject_reason');
    let request;

    if (photo.type === PHOTO_TYPE.UNSPLASH) {
      request = actions.photos.moderation.reject({
        id: photo.id,
        cause: rejectionCause,
      });

      setSelectedPhoto(null);

      setPhotos(photos => photos.map(x => (x.id === photo.id ? { ...x, _status: 6 } : x)));
    } else {
      const causeId = Number(rejectionCause);

      if (Number.isNaN(causeId)) {
        alert('Cause must be a number');
        failDecision(decision, { status: 400 });
        return;
      }

      request = actions.userPhotos.reject({
        ...photo,
        cause: causeId,
      });

      setSelectedUserPhoto(null);

      setUserPhotos(userPhotos =>
        userPhotos.map(x => (x.id === photo.id ? { ...x, _status: 2 } : x))
      );
    }

    request.then(result => completeDecision(decision, result)).catch(error => failDecision(decision, error));
    _moveToNextPhoto(nextPhotoToClick);
  };

  const _onAssign = async (_photo, _spot) => {
    const photo = _photo || selectedPhoto || selectedUserPhoto;
    const nextPhotoToClick = cloneDeep(photo);
    const spot = _spot || selectedSpot;
    const decision = beginDecision(
      photo,
      'assign_spot',
      spot,
      _photo ? 'easy_assign' : 'assign_spot'
    );

    if (!spot || !spot.isHydrated) {
      window.alert('Falta hidratar');
      failDecision(decision, { status: 400 });
      return;
    }

    const newPhoto = {
      ...photo,
      _status: photo.type === PHOTO_TYPE.UNSPLASH ? 5 : 1,
    };

    const newSpot = {
      ...spot,
    };

    if (photo.type === PHOTO_TYPE.UNSPLASH) {
      newSpot.photos = [...(spot.photos || []), photo.id];
      newSpot.photosHydrated = [...(spot.photosHydrated || []), photo];
    } else {
      newSpot.userPhotos = [...(spot.userPhotos || []), photo.id];
      newSpot.userPhotosHydrated = [...(spot.userPhotosHydrated || []), photo];
    }

    const p = actions.spots.assign(spot, newPhoto);
    actions.promises.add(p, 'short'); // small delay, because no revlidate, but new files

    // Optimistic: the UI has to move to the main photo picker right away, without
    // waiting for the assign request to settle.
    if (photo.type === PHOTO_TYPE.UNSPLASH) {
      setPhotos(photos => photos.map(x => (x.id === photo.id ? newPhoto : x)));
    } else {
      setUserPhotos(userPhotos => userPhotos.map(x => (x.id === photo.id ? newPhoto : x)));
    }

    setSelectedPhoto(null);
    setSelectedUserPhoto(null);
    setSpots(currentSpots =>
      currentSpots.map(currentSpot => {
        if (!spotsReferToSameSpot(currentSpot, spot)) {
          return currentSpot;
        }

        if (photo.type === PHOTO_TYPE.UNSPLASH) {
          return {
            ...currentSpot,
            photos: [...new Set([...(currentSpot.photos || []), photo.id])],
            photosHydrated: [
              ...(currentSpot.photosHydrated || []).filter(item => item.id !== photo.id),
              photo,
            ],
          };
        }

        return {
          ...currentSpot,
          userPhotos: [...new Set([...(currentSpot.userPhotos || []), photo.id])],
          userPhotosHydrated: [
            ...(currentSpot.userPhotosHydrated || []).filter(item => item.id !== photo.id),
            photo,
          ],
        };
      })
    );
    setSelectedSpot(null);
    setSpotWithNoMainPhotoId(newSpot);
    _moveToNextPhoto(nextPhotoToClick);

    try {
      const result = await p;

      completeDecision(decision, result);
    } catch (error) {
      failDecision(decision, error);

      if (photo.type === PHOTO_TYPE.UNSPLASH) {
        setPhotos(currentPhotos =>
          currentPhotos.map(item =>
            item.id === photo.id ? { ...item, _status: photo._status } : item
          )
        );
      } else {
        setUserPhotos(currentPhotos =>
          currentPhotos.map(item =>
            item.id === photo.id ? { ...item, _status: photo._status } : item
          )
        );
      }

      setSpots(currentSpots =>
        currentSpots.map(currentSpot => {
          if (!spotsReferToSameSpot(currentSpot, spot)) {
            return currentSpot;
          }

          return photo.type === PHOTO_TYPE.UNSPLASH
            ? {
                ...currentSpot,
                photos: (currentSpot.photos || []).filter(id => id !== photo.id),
                photosHydrated: (currentSpot.photosHydrated || []).filter(
                  item => item.id !== photo.id
                ),
              }
            : {
                ...currentSpot,
                userPhotos: (currentSpot.userPhotos || []).filter(id => id !== photo.id),
                userPhotosHydrated: (currentSpot.userPhotosHydrated || []).filter(
                  item => item.id !== photo.id
                ),
              };
        })
      );

      window.alert('Error en assign!: ' + error.message);
      console.log('$$ error', error, spot, photo);
    }
  };

  const _onSpotFormChange = data => {
    setName(data.name);
    setFrom(data.from);
    setSlug(data.slug);
    setCity(data.city);
    setCountry(data.country);
  };

  const _onCreateNewSpotClick = () => {
    setAction(ACTIONS.CREATE.SET_GPS);
    setIsGPSCorrect(false);
  };

  const _onRejectDrone = () => {
    setCause('Drone');
    _onReject('Drone');
  };

  const _onRejectBlah = () => {
    setCause('Blah');
    _onReject('Blah');
  };

  const _onRejectWrongGPS = () => {
    setCause('Wrong GPS');
    _onReject('Wrong GPS');
  };

  const _onGPSSet = () => {
    setAction(ACTIONS.CREATE.SPOT_FORM);
    setIsGPSCorrect(false);
  };

  const _onCreateSpotClick = () => {
    closePoiInformation();

    const photo = selectedPhoto || selectedUserPhoto;

    if (pendingCreationPhotoIds.current.has(photo.id)) {
      return;
    }

    const nextPhotoToClick = cloneDeep(photo);
    const decision = beginDecision(photo, 'create_spot', null, 'create_spot');
    let decisionSettled = false;

    if (!country) {
      window.alert('Missing country');
      failDecision(decision, { status: 400 });
      return;
    }

    if (!slug) {
      window.alert('Missing slug');
      failDecision(decision, { status: 400 });
      return;
    }

    let spot = {
      id: null,
      name,
      from,
      slug,
      country,
      lat: null,
      lng: null,
      photos: [],
      userPhotos: [],
      mainPhotoId: photo.id,
    };

    if (photo.type === PHOTO_TYPE.UNSPLASH) {
      spot.photos = [photo.id];
    } else {
      spot.userPhotos = [photo.id];
    }

    const position = isGPSCorrect ? getPhotoPosition(photo) : newGPS;

    if (!position || !Number.isFinite(Number(position.lat)) || !Number.isFinite(Number(position.lng))) {
      window.alert('Missing GPS coordinates');
      failDecision(decision, { status: 400 });
      return;
    }

    spot.lat = Number(position.lat);
    spot.lng = Number(position.lng);

    pendingCreationPhotoIds.current.add(photo.id);

    const { pendingSpot, creationPromise } = actions.spots.createOptimistically(spot);
    pendingSpot.photosHydrated = photo.type === PHOTO_TYPE.UNSPLASH ? [photo] : [];
    pendingSpot.userPhotosHydrated = photo.type === PHOTO_TYPE.USER ? [photo] : [];
    pendingSpot.pointType = 'spot';
    pendingSpot.isHydrated = true;

    actions.promises.add(creationPromise, 'long');
    setSpots(currentSpots => [...currentSpots, pendingSpot]);

    if (photo.type === PHOTO_TYPE.UNSPLASH) {
      setPhotos(currentPhotos =>
        currentPhotos.map(item => (item.id === photo.id ? { ...item, _status: 5 } : item))
      );
      setSelectedPhoto(null);
    } else {
      setUserPhotos(currentPhotos =>
        currentPhotos.map(item => (item.id === photo.id ? { ...item, _status: 1 } : item))
      );
      setSelectedUserPhoto(null);
    }

    setSpotWithNoIsTop(pendingSpot);
    setSpotWithNoDescription(pendingSpot);
    addPendingSpotToNearbyAssignments(pendingSpot, photo);
    _moveToNextPhoto(nextPhotoToClick);

    creationPromise
      .then(newSpot => {
        pendingCreationPhotoIds.current.delete(photo.id);
        completeDecision(decision, newSpot);
        decisionSettled = true;

        const replacePendingSpot = currentSpot => {
          if (!currentSpot || currentSpot._pendingCreationKey !== pendingSpot._pendingCreationKey) {
            return currentSpot;
          }

          return {
            ...newSpot,
            ...currentSpot,
            id: newSpot.id,
            _isPendingCreation: false,
          };
        };

        setSpots(currentSpots => currentSpots.map(replacePendingSpot));
        setSpotWithNoMainPhotoId(replacePendingSpot);
        setSpotWithNoIsTop(replacePendingSpot);
        setSpotWithNoDescription(replacePendingSpot);
      })
      .catch(error => {
        pendingCreationPhotoIds.current.delete(photo.id);
        if (!decisionSettled) {
          failDecision(decision, error);
        }

        setSpots(currentSpots =>
          currentSpots.filter(item => item._pendingCreationKey !== pendingSpot._pendingCreationKey)
        );
        setSpotWithNoMainPhotoId(currentSpot =>
          currentSpot && currentSpot._pendingCreationKey === pendingSpot._pendingCreationKey
            ? null
            : currentSpot
        );
        setSpotWithNoIsTop(currentSpot =>
          currentSpot && currentSpot._pendingCreationKey === pendingSpot._pendingCreationKey
            ? null
            : currentSpot
        );
        setSpotWithNoDescription(currentSpot =>
          currentSpot && currentSpot._pendingCreationKey === pendingSpot._pendingCreationKey
            ? null
            : currentSpot
        );

        const removePendingSpot = entry => ({
          ...entry,
          spots: entry.spots.filter(
            item => item._pendingCreationKey !== pendingSpot._pendingCreationKey
          ),
        });

        listOfPossibleToAssign = listOfPossibleToAssign
          .map(removePendingSpot)
          .filter(entry => entry.spots.length > 0);
        setPossibleToAssign(currentEntry => {
          const updatedEntry = currentEntry && removePendingSpot(currentEntry);

          if (updatedEntry && updatedEntry.spots.length > 0) {
            return updatedEntry;
          }

          if (listOfPossibleToAssign.length === 0) {
            return null;
          }

          const nextEntry = listOfPossibleToAssign[0];
          listOfPossibleToAssign = listOfPossibleToAssign.slice(1);
          return nextEntry;
        });

        if (photo.type === PHOTO_TYPE.UNSPLASH) {
          setPhotos(currentPhotos =>
            currentPhotos.map(item =>
              item.id === photo.id ? { ...item, _status: photo._status } : item
            )
          );
        } else {
          setUserPhotos(currentPhotos =>
            currentPhotos.map(item =>
              item.id === photo.id ? { ...item, _status: photo._status } : item
            )
          );
        }

        window.alert('Error!: ' + error.message);
        console.log('$$ error', error, spot, photo);
      });
  };

  const getAddresses = async (lat, lng) => {
    const r = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&language=en&key=${config.googleMaps.apiKey}`
    );
    const json = await r.json();
    setAddresses(json.results);
  };

  const _onMapClick = ({ lat, lng, ...other }) => {
    // it could be `null`, array, or `true`
    if (addresses === true) {
      getAddresses(lat, lng);
    } else if (action === ACTIONS.CREATE.SET_GPS || action === ACTIONS.EDIT) {
      setNewGPS({ lat, lng });
    } else if (isSettingSW) {
      setCoors({
        ...coors,
        sw: {
          lat,
          lng,
        },
      });

      setIsSettingNE(false);
      setIsSettingSW(false);
    } else if (isSettingNE) {
      setCoors({
        ...coors,
        ne: {
          lat,
          lng,
        },
      });

      setIsSettingNE(false);
      setIsSettingSW(false);
    }
  };

  useEffect(() => {
    const pointsToShow = [...cityTours];

    if (showPendings) {
      if (showNulls) {
        pointsToShow.push(...photos.filter(x => x._status === 3));
      } else {
        pointsToShow.push(...photos.filter(x => x.lat !== null && x._status === 3));
      }
    }

    if (showPendingsFromUsers) {
      pointsToShow.push(...userPhotos.filter(x => x._status === 0));
    }

    if (showRejecteds) {
      pointsToShow.push(...userPhotos.filter(x => x._status === 2));
    }

    if (showSpots) {
      pointsToShow.push(...spots);
    }

    if (isNewPositionShown) {
      pointsToShow.push({ ...newGPS, pointType: 'crosshair' });
    }

    setPointsToShow(pointsToShow);
  }, [
    isNewPositionShown,
    spots,
    userPhotos,
    photos,
    showPendings,
    showSpots,
    showNulls,
    showPendingsFromUsers,
    showRejecteds,
    isGPSCorrect,
    newGPS,
    cityTours,
  ]);

  const onPendingsFilterClick = async () => {
    if (photos.length === 0) {
      await getUnsplashPhotosPending();
    }

    setShowPendings(!showPendings);
  };

  const onPendingsFromUsersFilterClick = async () => {
    if (userPhotos.length === 0) {
      await getUserPhotosPending();
    }

    setShowPendingsFromUsers(!showPendingsFromUsers);
  };

  const onDetectiveFilterClick = async () => {
    if (photos.length === 0) {
      await getDetectivePhotosPending();
    }

    setShowPendings(!showPendings);
  };

  // const onRejectedsFilterClick = async () => {
  //   if (photos.length === 0) {
  //     await getUnsplashPhotosPending();
  //   }

  //   setShowRejecteds(!showRejecteds);
  // };

  const onSpotsFilterClick = async () => {
    if (spots.length < 20) {
      await getSpots();
    }

    setShowSpots(!showSpots);
  };

  const onCoorsChange = newData => {
    setCoors({ ...coors, ...newData });
    setPhotos([]);
  };

  const _onEditSpotClick = () => {
    const spot = selectedSpot;

    setId(spot.id);
    setName(spot.name);
    setFrom(spot.from);
    setSlug(spot.slug);
    setCity('');
    setCountry(spot.country);
    setIsGPSCorrect(false);
    setNewGPS({
      lat: spot.lat,
      lng: spot.lng,
    });
    setAction(ACTIONS.EDIT);
  };

  const _onCreateCityTourClick = async () => {
    const { lat, lng } = center;

    const r = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&result_type=locality&language=en&key=${config.googleMaps.apiKey}`
    );
    const json = await r.json();
    const parts = json.results[json.results.length - 1].address_components;

    const locality = (parts.find(x => x.types.includes('locality')) || parts[0]).long_name;
    const country = parts.find(x => x.types.includes('country')).long_name;

    const city = `${locality}, ${country}`;

    if (!city) {
      window.alert('Error');
      console.log(json);
      return;
    }

    if (!window.confirm(`${city}?`)) {
      return;
    }

    setCityTours([
      ...cityTours,
      {
        id: Math.random(),
        city: {
          en: city,
        },
        lat: Number(center.lat.toFixed(4)),
        lng: Number(center.lng.toFixed(4)),
        type: 'CITYTOUR',
        pointType: 'citytour',
      },
    ]);

    actions.cityTours.add({
      city: {
        en: city,
      },
      location: {
        lat: Number(center.lat.toFixed(4)),
        lng: Number(center.lng.toFixed(4)),
      },
    });
  };

  const _onSaveSpotClick = () => {
    const spot = selectedSpot;
    const newData = {
      name,
      from,
      slug,
      country,
      lat: newGPS.lat,
      lng: newGPS.lng,
    };

    setIsCreating(true);
    const p = actions.spots
      .edit(spot, newData)
      .then(() => {
        setIsCreating(false);
      })
      .catch(error => {
        console.error(error);
        window.alert('Error editing!: ' + error.message);
      });

    actions.promises.add(p, 'long');

    setSpots(spots => spots.map(x => (x.id === spot.id ? { ...spot, ...newData } : x)));

    reset();
  };

  const onMainPhotoIdChange = ({ spotId, mainPhotoId }) => {
    setSpots(spots => {
      spots.find(x => x.id === spotId).mainPhotoId = mainPhotoId;
      return [...spots];
    });
    setSpotWithNoMainPhotoId(null);
  };

  const onIsTopPhotoChange = ({ spotId, isTop }) => {
    setSpots(spots => {
      spots.find(x => x.id === spotId).isTop = isTop;
      return [...spots];
    });
    setSpotWithNoIsTop(null);
  };

  const onDescriptionChange = ({ spotId, description, spot: changedSpot }) => {
    setSpots(currentSpots =>
      currentSpots.map(currentSpot =>
        currentSpot.id === spotId || currentSpot._pendingCreationKey === spotId
          ? { ...currentSpot, description }
          : currentSpot
      )
    );
    setSpotWithNoDescription(currentSpot => {
      const isCurrentSpot =
        currentSpot &&
        (currentSpot.id === spotId || currentSpot._pendingCreationKey === spotId);

      if (!isCurrentSpot) {
        if (description === null && changedSpot) {
          return { ...changedSpot, description };
        }

        return currentSpot;
      }

      return description === null ? { ...currentSpot, description } : null;
    });
  };

  const _NeedsMoreInfo = async photo => {
    const decision = beginDecision(photo, 'needs_info', null, 'needs_info');

    try {
      if (photo.type === PHOTO_TYPE.UNSPLASH) {
        await actions.photos.editById(photo.id, { detectiveNeedsMoreData: true });
        setPhotos(photos.filter(x => x.id !== photo.id));
        setSelectedPhoto(null);
      } else {
        setUserPhotos(userPhotos.filter(x => x.id !== photo.id));
        setSelectedUserPhoto(null);
      }

      completeDecision(decision);
      _moveToNextPhoto(photo);
    } catch (error) {
      failDecision(decision, error);
      window.alert('Error on detectiveNeedsMoreInfo: ' + error.message);
    }
  };

  const photo = selectedPhoto || selectedUserPhoto;
  const spot = selectedSpot;
  const spotFormPosition = isGPSCorrect ? getPhotoPosition(photo) : newGPS;
  const isUnsplash = photo && photo.type === PHOTO_TYPE.UNSPLASH;

  let mapActions = [];

  if (photo && action === ACTIONS.NONE) {
    mapActions = [
      <button
        key={0}
        autoFocus
        data-action-key="1"
        className="approve"
        onClick={_onCreateNewSpotClick}
      >
        CREATE (1)
      </button>,
      <button
        key={1}
        data-action-key="2"
        className="approve"
        onClick={async () => {
          // const visibleSpots = document.querySelectorAll('.Map .SpotPoint');
          // const onlyOne = visibleSpots.length === 1;
          // setAction(ACTIONS.ASSIGN);
          // setTimeout(() => {
          //   visibleSpots[0].click();
          // }, 10);

          let nearSpots = [];

          spots.forEach(spot => {
            let distance = haversine(
              photo.type === PHOTO_TYPE.UNSPLASH ? photo.location.position : photo.location,
              spot
            );

            if (photo.detective) {
              photo.detective.forEach(detective => {
                distance = Math.min(distance, haversine(detective.position, spot));
              });
            }

            if (distance < 300) {
              nearSpots.push({ ...spot, distance });
            }
          });

          if (nearSpots.length > 0) {
            nearSpots.sort((a, b) => a.distance - b.distance);

            const proms = nearSpots.map(async spot => {
              await hydrateSpot(spot);
            });

            await Promise.all(proms);

            setPossibleToAssign({ photo, spots: nearSpots });
          } else {
            setAction(ACTIONS.ASSIGN);
          }
        }}
      >
        ASSIGN (2)
      </button>,
      <button
        key={2}
        data-action-key="3"
        className="reject"
        onClick={() => setAction(ACTIONS.REJECT)}
      >
        REJECT (3)
      </button>,
      <button key={3} data-action-key="4" className="reject" onClick={() => _NeedsMoreInfo(photo)}>
        NEEDS INFO (4)
      </button>,
    ];
  } else if (spot && ![ACTIONS.NONE, ACTIONS.EDIT, ACTIONS.CREATE.SPOT_FORM].includes(action)) {
    mapActions = [
      <button key={0} data-action-key="1" className="approve" onClick={_onEditSpotClick}>
        EDIT (1)
      </button>,
    ];
  } else {
    mapActions = [
      <button key={0} className="approve" onClick={_onCreateCityTourClick}>
        CREATE CITY TOUR
      </button>,
    ];
  }

  return (
    <div className="Moderation">
      <div className={`left ` + (action !== ACTIONS.NONE ? '--showingForm' : '')}>
        {!!photo && action === ACTIONS.NONE && (
          <Fragment>
            <img
              className="photo"
              src={getModerationPhotoUrl(photo)}
              data-id={photo.id}
              alt="Selected"
            />
            <div className="photo-description">
              {isUnsplash && (
                <Fragment>
                  {photo.description && <div>Description: {photo.description}</div>}
                  <div>
                    Location: [{photo.location.name}] [{photo.location.title}]
                  </div>
                </Fragment>
              )}

              {!isUnsplash && <div>{photo.user.username}</div>}
            </div>
          </Fragment>
        )}

        {!!spot && action === ACTIONS.SHOW_SPOT && (
          <div className="slider">
            <div className="quantity">
              {spot.photosHydrated.length} unsplash photos, {spot.userPhotosHydrated.length} user
              photos
            </div>

            <Slider
              key={spot.id}
              photos={[
                ...spot.userPhotosHydrated.map(x => x.url),
                ...spot.photosHydrated.map(x => x.urls.regular),
              ]}
            />
          </div>
        )}

        {action === ACTIONS.CREATE.SET_GPS && (
          <div className="gps-set">
            <h1>Click on the map to set the new GPS position</h1>
            <div>
              GPS point: Lat {newGPS.lat} Lng {newGPS.lng}
            </div>
            <button autoFocus data-action-key="1" className="approve" onClick={_onGPSSet}>
              Done (1)
            </button>
          </div>
        )}

        {action === ACTIONS.CREATE.SPOT_FORM && (
          <div className="create-spot">
            <SpotForm
              spots={spots}
              onChange={_onSpotFormChange}
              onSubmit={() => {
                setTimeout(() => {
                  document.querySelector('button.approve').focus();
                }, 0);
              }}
              id={id}
              name={name}
              from={from}
              slug={slug}
              city={city}
              country={country}
              lat={spotFormPosition && spotFormPosition.lat}
              lng={spotFormPosition && spotFormPosition.lng}
              poi={selectedPoi}
              photo={selectedPhoto || selectedUserPhoto}
            />

            {!!photo && !!photo.appeal && !!photo.appeal.text && (
              <div className="moderation-appeal">
                <strong>
                  The uploader disagrees with this rejection
                  {Number.isFinite(Number(photo.appeal.rejectedCause))
                    ? ` (${getRejectionCauseLabel(photo.appeal.rejectedCause)})`
                    : ''}
                </strong>
                <p>{photo.appeal.text}</p>
              </div>
            )}

            {!!photo && (
              <div>
                {photo.description && <div>Description: {photo.description}</div>}

                {(!!photo.location.name || !!photo.location.title) && (
                  <div>
                    Location: [{photo.location.name}] [{photo.location.title}]
                  </div>
                )}

                {Array.isArray(addresses) && (
                  <div>
                    {[...new Set(addresses.map(x => x.formatted_address))].map(x => (
                      <div>{x}</div>
                    ))}
                  </div>
                )}

                {(Array.isArray(addresses) || addresses === null) && (
                  <button data-action-key="9" onClick={() => setAddresses(true)}>
                    Addresses (9)
                  </button>
                )}

                {addresses === true && <div>Click on the map to get the address</div>}

                {photo.detective &&
                  photo.detective.map(item => (
                    <div key={item.whoId} style={{ marginTop: '8px' }}>
                      Name: {item.name}, from: {item.from} [DELETE]
                    </div>
                  ))}
              </div>
            )}

            <button style={{ marginTop: '16px' }} className="cancel" onClick={() => reset()}>
              Cancel
            </button>

            <button
              autoFocus={!!name && !!from}
              className="approve"
              disabled={isCreating}
              onClick={_onCreateSpotClick}
            >
              {isCreating ? 'Creating...' : 'Create'}
            </button>

            <br />

            {!!photo && (
              <img
                className="photo"
                src={getModerationPhotoUrl(photo)}
                data-id={photo.id}
                alt="Selected"
              />
            )}
          </div>
        )}

        {action === ACTIONS.ASSIGN && (
          <div className="assign-form">
            <div>The photo is going to be assigned to:</div>

            {assignTo ? (
              <div>
                Spot Name: "{assignTo.name}" from "{assignTo.from}"
              </div>
            ) : (
              <div>Select a Spot</div>
            )}

            {assignTo && (
              <button
                data-action-key="1"
                disabled={isCreating}
                className="assign"
                onClick={() => _onAssign()}
              >
                Yes, Assign it (1)
              </button>
            )}

            <button
              data-action-key="2"
              className="cancel"
              onClick={() =>
                reset(() => {
                  setSelectedPhoto(selectedPhoto);
                  setSelectedUserPhoto(selectedUserPhoto);
                })
              }
            >
              Cancel (2)
            </button>

            <br />
            <br />

            {assignTo && (
              <>
                {(assignTo.photosHydrated || []).map(x => (
                  <img
                    className="assign_photo"
                    key={x.id}
                    data-id={x.id}
                    src={x.urls.regular}
                    alt={assignTo.name}
                  />
                ))}

                {(assignTo.userPhotosHydrated || []).map(x => (
                  <img
                    className="assign_photo"
                    key={x.id}
                    data-id={x.id}
                    src={x.url}
                    alt={assignTo.name}
                  />
                ))}
              </>
            )}
          </div>
        )}

        {action === ACTIONS.REJECT && isUnsplash && (
          <div className="reject-form">
            <textarea value={cause} onChange={e => setCause(e.target.value)} placeholder="Cause" />
            <button className="reject" onClick={() => _onReject()}>
              Reject
            </button>

            <br />
            <br />
            <br />
            <br />

            <button data-action-key="2" className="reject" onClick={_onRejectWrongGPS}>
              Wrong GPS (2)
            </button>
            <br />
            <br />
            <button data-action-key="3" className="reject" onClick={_onRejectBlah}>
              Blah (3)
            </button>
            <br />
            <br />
            <button className="reject" onClick={_onRejectDrone}>
              Drone
            </button>
            <br />
            <br />
            <button className="cancel" onClick={reset}>
              Cancel
            </button>
          </div>
        )}

        {action === ACTIONS.REJECT && !isUnsplash && (
          <div className="reject-form">
            {REJECTION_CAUSES.map(x => (
              <button
                key={x.value}
                className={cause === x.value ? 'approve' : 'reject'}
                data-action-key={x['data-action-key']}
                onClick={() => setCause(x.value)}
              >
                {x.label}
                {x['data-action-key'] && ` (${x['data-action-key']})`}
              </button>
            ))}

            <br />
            <br />

            <button className="reject" data-action-key="Enter" onClick={() => _onReject()}>
              Reject
            </button>
          </div>
        )}

        {action === ACTIONS.EDIT && (
          <div className="edit-spot-form">
            <SpotForm
              spots={spots}
              onChange={_onSpotFormChange}
              onSubmit={() => {
                setTimeout(() => {
                  document.querySelector('button.approve').focus();
                }, 0);
              }}
              id={id}
              name={name}
              from={from}
              slug={slug}
              city={city}
              country={country}
              lat={newGPS.lat}
              lng={newGPS.lng}
              poi={selectedPoi}
              photo={selectedPhoto || selectedUserPhoto}
            />

            {newGPS.lat && (
              <div>
                New GPS point: Lat {newGPS.lat} Lng {newGPS.lng}
              </div>
            )}

            <button className="cancel" onClick={reset}>
              Cancel
            </button>
            <button className="approve" onClick={_onSaveSpotClick}>
              Save
            </button>
          </div>
        )}
      </div>

      <div className="right">
        <div className="filters">
          <input type="checkbox" checked={showPendings} onChange={onPendingsFilterClick} /> Unsplash
          <input
            type="checkbox"
            checked={showPendingsFromUsers}
            onChange={onPendingsFromUsersFilterClick}
          />{' '}
          Users
          <input type="checkbox" checked={showPendings} onChange={onDetectiveFilterClick} />{' '}
          Detective
          {/* <input type="checkbox" checked={showRejecteds} onChange={onRejectedsFilterClick} />{' '}
          Rejected */}
          <input type="checkbox" checked={showSpots} onChange={onSpotsFilterClick} /> Spots
          {/* <input
            type="checkbox"
            checked={showNulls}
            onChange={() => setShowNulls(!showNulls)}
          />{' '}
          Nulls */}
          {' | '}
          <span onClick={() => setIsSettingSW(true)}>SW</span>:
          <input
            className="coor"
            type="text"
            onChange={e => onCoorsChange({ sw: { lat: e.target.value, lng: coors.sw.lng } })}
            value={coors.sw.lat}
          />
          <input
            className="coor"
            type="text"
            onChange={e => onCoorsChange({ sw: { lat: coors.sw.lat, lng: e.target.value } })}
            value={coors.sw.lng}
          />{' '}
          | <span onClick={() => setIsSettingNE(true)}>NE</span>:
          <input
            className="coor"
            type="text"
            onChange={e => onCoorsChange({ ne: { lat: +e.target.value, lng: coors.ne.lng } })}
            value={coors.ne.lat}
          />
          <input
            className="coor"
            type="text"
            onChange={e => onCoorsChange({ ne: { lat: coors.ne.lat, lng: +e.target.value } })}
            value={coors.ne.lng}
          />
        </div>

        <div className="map">
          <MapComponent
            selectedPhoto={selectedPhoto || selectedUserPhoto || {}}
            selectedSpot={selectedSpot || {}}
            points={pointsToShow}
            cityTours={cityTours}
            spots={showSpots ? spots : []}
            center={center}
            zoom={zoom}
            onChange={_onMapChange}
            onClick={_onMapClick}
            actions={mapActions}
            onPointClick={_onPointClick}
            onPointHover={_onPointHover}
            onPoiClick={canFillSpotFromPoi ? setSelectedPoi : null}
            onSpotClick={_onSpotClick}
            hideSpots={
              canFillSpotFromPoi ||
              spotWithNoMainPhotoId ||
              spotWithNoIsTop ||
              spotWithNoDescription ||
              possibleToAssign
            }
          />
        </div>
      </div>

      {spotWithNoMainPhotoId && (
        <MainPhotoForm
          spot={spotWithNoMainPhotoId}
          onChange={onMainPhotoIdChange}
          onClose={() => setSpotWithNoMainPhotoId(null)}
        />
      )}

      {!spotWithNoMainPhotoId && spotWithNoIsTop && (
        <TopPhotoForm
          key={spotWithNoIsTop.id}
          spot={spotWithNoIsTop}
          onChange={onIsTopPhotoChange}
          onClose={() => setSpotWithNoIsTop(null)}
        />
      )}

      {!spotWithNoMainPhotoId && !spotWithNoIsTop && spotWithNoDescription && (
        <DescriptionForm
          key={spotWithNoDescription.id}
          spot={spotWithNoDescription}
          onChange={onDescriptionChange}
        />
      )}

      {!spotWithNoMainPhotoId && !spotWithNoIsTop && !spotWithNoDescription && possibleToAssign && (
        <EasyToAssign
          key={possibleToAssign.photo.id}
          photo={possibleToAssign.photo}
          spots={possibleToAssign.spots}
          onYes={spot => {
            _onAssign(possibleToAssign.photo, spot);

            if (listOfPossibleToAssign.length > 0) {
              setPossibleToAssign(listOfPossibleToAssign[0]);
              listOfPossibleToAssign = listOfPossibleToAssign.slice(1);
            } else {
              setPossibleToAssign(null);
            }
          }}
          onNo={() => {
            if (listOfPossibleToAssign.length > 0) {
              setPossibleToAssign(listOfPossibleToAssign[0]);
              listOfPossibleToAssign = listOfPossibleToAssign.slice(1);
            } else {
              setPossibleToAssign(null);
              setAction(ACTIONS.ASSIGN);
            }
          }}
        />
      )}

      <div className="pendingPromisses">
        <PendingPromisses />
      </div>
    </div>
  );
};

export default Moderation;
